/**
 * IL FORMATO DEL LOG DI UN JOB, DEFINITO UNA VOLTA SOLA.
 *
 * Il log lo SCRIVE il worker (recordJobProgress) e lo RILEGGE il pannello in admin
 * (JobLiveMonitor, per dare a ogni evento la sua ora locale, il suo livello e il suo tempo di
 * permanenza). Finché le due metà tenevano ciascuna la propria idea del formato — un template
 * literal di qua, una regex di là — erano libere di divergere senza che niente se ne accorgesse:
 * il giorno che il worker avesse cambiato separatore, il pannello avrebbe smesso di riconoscere
 * gli eventi e sarebbe tornato a mostrare un muro di testo, cioè esattamente la cosa che questo
 * lavoro voleva togliere di mezzo. Qui stanno entrambe, e il test le fa girare in tondo.
 *
 * Forma di un evento:
 *
 *   2026-08-22T14:27:22.331Z INFO [Ricerca e bozza] Ricerca fonti, scraping e generazione
 *       (righe successive del medesimo evento, indentate di quattro spazi)
 *
 * L'indentazione è l'unica cosa che distingue la continuazione di un evento dall'evento nuovo, ed
 * è il motivo per cui i messaggi multiriga (la diagnosi, i rilievi del gate) restano leggibili
 * senza rompere la scansione per righe.
 */

export type JobLogLevel = 'INFO' | 'WARN' | 'ERROR'

export type JobLogEntry = {
  /** Istante dell'evento. */
  at: Date
  level: JobLogLevel
  stage: string
  /** Corpo dell'evento, con le continuazioni già riunite e de-indentate. */
  body: string
}

/** Indentazione delle righe di continuazione. Non cambiarla senza cambiare anche il parser. */
const INDENT = '    '
const STAGE_MAX = 160
const MESSAGE_MAX = 4000

/**
 * Costruisce la riga (o il blocco di righe) da appendere al log. Termina sempre con `\n`, così il
 * chiamante può concatenare senza pensarci.
 */
export function buildLogLine(level: JobLogLevel, stage: string, message: string, at: Date = new Date()): string {
  const cleanStage = stage.replace(/[\r\n]+/g, ' ').slice(0, STAGE_MAX)
  const clean = String(message).replace(/\r/g, '').slice(0, MESSAGE_MAX)
  const [head = '', ...rest] = clean.split('\n')
  return (
    [`${at.toISOString()} ${level} [${cleanStage}] ${head}`, ...rest.map((r) => (r.trim() ? `${INDENT}${r}` : ''))].join('\n') + '\n'
  )
}

const HEAD = /^(\d{4}-\d{2}-\d{2}T[\d:.]+Z) (INFO|WARN|ERROR) \[([^\]]*)\] ?(.*)$/

/**
 * Ricompone il log in eventi. Le righe che non aprono un evento appartengono al precedente; se il
 * log comincia con spazzatura (troncamento a 64 KiB che ha tagliato a metà un blocco) quelle righe
 * si scartano: meglio perdere una coda orfana che attribuirla all'evento sbagliato.
 */
export function parseJobLog(raw: string | null | undefined): JobLogEntry[] {
  const out: JobLogEntry[] = []
  // Il `\n` che CHIUDE l'ultimo evento non è una riga vuota dentro il blocco: se lo si lascia allo
  // split diventa un elemento vuoto in coda e si porta dietro un a-capo che non c'era nel messaggio
  // originale. Le righe vuote INTERNE, che nel blocco di diagnosi separano le sezioni, restano.
  const text = String(raw ?? '').replace(/\n$/, '')
  if (!text) return out
  for (const line of text.split('\n')) {
    const m = line.match(HEAD)
    if (m) {
      out.push({ at: new Date(m[1]), level: m[2] as JobLogLevel, stage: m[3], body: m[4] })
    } else if (out.length && line.trim()) {
      out[out.length - 1].body += `\n${line.startsWith(INDENT) ? line.slice(INDENT.length) : line}`
    } else if (out.length) {
      out[out.length - 1].body += '\n'
    }
  }
  return out
}

/** `2m 14s` / `12s`. Per il tempo speso da una fase. */
export function humanDuration(ms: number): string {
  const s = Math.max(0, Math.round(ms / 1000))
  if (s < 60) return `${s}s`
  return `${Math.floor(s / 60)}m ${String(s % 60).padStart(2, '0')}s`
}
